'use client';

import type Trail from '@alltrails/core/types/Trail';
import classNames from 'classnames';
import { useState, lazy } from 'react';
import { NoSsrSuspense, useWindowSize, Image } from '@alltrails/core';
import { BREAKPOINT_MD_SIZE } from '@alltrails/denali/tokens';
import styles from './TopResults.module.scss';
import usePhotos from '@/hooks/usePhotos';

const SwiperCarousel = lazy(() => import('@alltrails/denali/components/SwiperCarousel'));

const carouselBreakpoint = 1440;
const sizes = `(max-width: ${BREAKPOINT_MD_SIZE}px) 64px, (max-width: ${carouselBreakpoint}px) 180px, 240px`;

type Props = {
  trail: Trail;
  defaultUrl?: string;
};

const TrailCarousel = ({ trail, defaultUrl }: Props) => {
  const [cardHovered, setCardHovered] = useState(false);
  const { width } = useWindowSize();
  const isStaticImage = !!width && width < carouselBreakpoint;
  const isSmallSquare = !!width && width <= BREAKPOINT_MD_SIZE;
  const staticImageDimensions = isSmallSquare ? 64 : 180;
  const { photos, loadPhotos } = usePhotos(trail);

  const onMouseEnter = () => {
    loadPhotos();
    setCardHovered(true);
  };

  const onMouseLeave = () => {
    setCardHovered(false);
  };

  const firstImageSrc = defaultUrl ?? photos[0];
  const staticFirstImage = (
    <div className={styles.imageContainer} data-testid={`${trail.id}-one-photo`}>
      {firstImageSrc && (
        <Image
          alt={trail.name}
          src={firstImageSrc}
          className={styles.image}
          sizes={sizes}
          loading="lazy"
          unoptimized
          width={staticImageDimensions}
          height={staticImageDimensions}
        />
      )}
    </div>
  );

  if (isStaticImage) {
    return staticFirstImage;
  }

  return (
    <div className={styles.resultImage} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} data-testid="trail-carousel">
      {!!photos.length && (
        <NoSsrSuspense fallback={staticFirstImage}>
          <SwiperCarousel
            className={classNames(styles.imageContainer, cardHovered && styles.zoomPhoto)}
            uniqueId={`${trail.id}`}
            items={photos}
            hasPaginationGradient
            renderItem={(src, index) => (
              <Image
                alt={`${trail.name}-${index}`}
                src={src}
                className={styles.image}
                sizes={sizes}
                loading="lazy"
                unoptimized
                width={240}
                height={180}
                handleLoadingState
              />
            )}
            getItemKey={(src, index) => `PhotoCarousel_${index}_${src}`}
            onFirstTouch={loadPhotos}
            arrowStyle="subtle"
            loop
            showPaginationDots
            hideSlideOverflow
          />
        </NoSsrSuspense>
      )}
    </div>
  );
};

export default TrailCarousel;
